import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import Button from '../components/common/Button';
import { sendVerificationOTP, confirmVerificationOTP } from '../api';

const VerifyEmail = () => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [timer, setTimer] = useState(0);
  
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { addToast } = useToast();
  
  const email = location.state?.email || '';

  useEffect(() => {
    if (!email) {
      addToast('No email found to verify. Please login or register.', 'error');
      navigate('/login');
    }
  }, [email, navigate, addToast]);

  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleChange = (element, index) => {
    if (isNaN(element.value)) return false;
    
    const newOtp = [...otp];
    newOtp[index] = element.value;
    setOtp(newOtp);

    // Focus next input
    if (element.value && element.nextSibling) {
      element.nextSibling.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && e.target.previousSibling) {
      e.target.previousSibling.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otpValue = otp.join('');
    if (otpValue.length !== 6) {
      addToast('Please enter complete 6-digit OTP', 'error');
      return;
    }
    
    setLoading(true);
    try {
      await confirmVerificationOTP(email, otpValue);
      addToast('Email verified successfully!', 'success');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      addToast(err.response?.data?.detail || 'Invalid or expired OTP', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (timer > 0) return;
    
    setResending(true);
    try {
      await sendVerificationOTP(email);
      addToast('OTP resent successfully', 'success');
      setTimer(60);
    } catch (err) {
      addToast(err.response?.data?.detail || 'Failed to resend OTP', 'error');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card text-center">
        <div className="w-16 h-16 bg-primary-light text-primary rounded-full flex items-center justify-center text-3xl mx-auto mb-6">
          ✉️
        </div>
        
        <h1 className="auth-title">Verify your email</h1>
        <p className="auth-subtitle max-w-sm mx-auto">
          We've sent a 6-digit verification code to<br/>
          <strong className="text-gray-900">{email}</strong>
        </p>
        
        <form onSubmit={handleSubmit} className="mt-8">
          <div className="otp-inputs mb-8">
            {otp.map((data, index) => (
              <input
                className={`otp-input ${data ? 'filled' : ''}`}
                type="text"
                name="otp"
                maxLength="1"
                key={index}
                value={data}
                onChange={e => handleChange(e.target, index)}
                onFocus={e => e.target.select()}
                onKeyDown={e => handleKeyDown(e, index)}
              />
            ))}
          </div>
          
          <Button type="submit" variant="primary" className="w-full" size="lg" isLoading={loading}>
            Verify Email
          </Button>
        </form>
        
        <div className="mt-8 pt-6 border-t border-gray-100 text-sm text-gray-500">
          Didn't receive the code?{' '}
          {timer > 0 ? (
            <span className="font-semi text-gray-400">Resend in {timer}s</span>
          ) : (
            <button 
              type="button" 
              onClick={handleResend}
              className="font-semi text-primary hover:underline"
              disabled={resending}
            >
              {resending ? 'Sending...' : 'Click to resend'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
